import { useId } from 'react';
import { CharCounter } from '@/components/CharCounter';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

/**
 * One labelled field: a name above it, a limit beside the name, and a refusal
 * under it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE CONTROL IS A FUNCTION AND NOT A CHILD. Everything this component exists
 * to guarantee is a set of matching `id`s — `htmlFor` on the label, `id` on the
 * control, `aria-describedby` pointing at the error, `aria-invalid` when there is
 * one — and a component that merely renders `{children}` cannot guarantee any of
 * it. Passing the wiring INTO the control is the difference between a helper that
 * makes the accessible version easy and one that makes it the only version;
 * spreading `props` is not optional, because without it there is no label.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * THE LABEL IS ALWAYS THERE, AND IS NOT A PLACEHOLDER. A placeholder is gone the
 * moment somebody types, which is exactly when a half-finished form most needs to
 * say what its fields are — and it sits at a contrast deliberately below the one a
 * label needs. Fields here carry a placeholder only where it shows the SHAPE of an
 * answer rather than repeating the label.
 *
 * OPTIONAL IS MARKED ONCE, ON THE LABEL, in the same words every time. The
 * alternative that this product must not have is a sentence under the control
 * explaining what the field is for (docs/1 § Copy in the interface): a label that
 * needs a paragraph is a label that needs rewriting.
 *
 * THE ERROR IS `role="alert"` AND IT RESERVES NO SPACE. Announced when it appears,
 * because somebody who cannot see the red line has to hear it; and in the flow
 * rather than absolutely positioned, because a form that jumps when a message
 * lands is a form that moves the button out from under a thumb.
 */
export function Field({
  label,
  optional,
  hint,
  error,
  counter,
  className,
  children,
}: {
  label: string;
  /** Marked on the label, never in prose beneath the control. */
  optional?: boolean;
  /**
   * A CONSTRAINT, in two or three words, beside the label — "12 characters".
   *
   * Not a description, not reassurance, and never a sentence: this is for the rule
   * that decides whether the field is accepted, said where the choice is being
   * made. Anything that reads as teaching belongs nowhere on the screen (docs/1
   * § Copy in the interface), and anything longer than a few words is that.
   */
  hint?: string;
  /** The refusal, in the words of whoever refused it. */
  error?: string | null;
  /** How much room is left, once there is little enough for that to matter. */
  counter?: { remaining: number; show: boolean };
  className?: string;
  children: (props: {
    id: string;
    'aria-invalid': true | undefined;
    'aria-describedby': string | undefined;
  }) => React.ReactNode;
}) {
  const id = useId();
  const errorId = `${id}-error`;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {/* The label and the counter share one row, so a counter appearing costs no
          height and moves nothing. `justify-between` puts the count on the far
          side under both directions without an `rtl:` override. */}
      <div className="flex items-baseline justify-between gap-2 px-1">
        <Label htmlFor={id}>
          {label}
          {(optional || hint) && (
            <span className="font-normal text-muted-foreground/70">
              {optional ? 'Optional' : hint}
            </span>
          )}
        </Label>
        {counter && (
          <CharCounter
            remaining={counter.remaining}
            show={counter.show}
            over={counter.remaining < 0}
          />
        )}
      </div>

      {children({
        id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': error ? errorId : undefined,
      })}

      {error && (
        <p id={errorId} role="alert" className="px-1 text-[13px] text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
